import React, { useEffect, useState, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  NotificationsActive as BellIcon,
  Flag as FlagIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { subDays, differenceInHours, differenceInDays } from "date-fns";
import { apiClient } from "../../utils/apiClient";
import { DobbyShipmentPage } from "../DobbyShipmentPage";
import { useSearchParams } from "react-router-dom";
import { ShipmentReminderComponent } from "../ShipmentReminderComponent";
import { EmailThreadComponent } from "@/components/EmailThreadComponent";
import { EventLogger } from "@/utils/eventLogger";
import {
  ReminderWithUser,
  ReminderDefinition,
  ListReminderTypesResponse,
} from "@services/shipments/reminders/types";

interface OrgUser {
  id: string;
  email: string;
  name: string;
  givenName: string;
}

interface ReminderSection {
  type: "separator" | "reminders";
  label?: string;
  reminders?: ReminderWithUser[];
}

interface ExceptionsDashboardContentProps {
  onShipmentClick?: (shipmentId: string) => void;
}

const ExceptionsDashboardContent: React.FC<ExceptionsDashboardContentProps> = ({
  onShipmentClick,
}) => {
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");

  // State
  const [reminders, setReminders] = useState<ReminderWithUser[]>([]);
  const [reminderSections, setReminderSections] = useState<ReminderSection[]>(
    []
  );
  const [reminderTypes, setReminderTypes] = useState<ReminderDefinition[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // email
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);

  // Load more
  const ITEMS_PER_PAGE = 100;

  // Filters
  const [filterUserIds, setFilterUserIds] = useState<string[]>([]);
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(
    subDays(new Date(), 30)
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Category filters (show/hide exceptions and milestones)
  const [showExceptions, setShowExceptions] = useState(true);
  const [showMilestones, setShowMilestones] = useState(true);

  const handleOpenEmail = (eventEmailId: string) => {
    EventLogger.getInstance().track({
      name: "click",
      action: "open_mail",
    });
    setOpenEmailId(eventEmailId);
  };

  const handleCloseEmail = () => {
    setOpenEmailId(null);
  };

  // Define fetch functions first (before useEffects that use them)
  const fetchReminderTypes = async () => {
    try {
      const res = await apiClient.post<ListReminderTypesResponse>(
        "/shipments/reminders/list-reminder-types",
        {}
      );
      setReminderTypes(res.types);
      // By default, select all types
      setFilterTypes(res.types.map((t) => t.type));
    } catch (error) {
      console.error("Error fetching reminder types:", error);
    }
  };

  const fetchOrgUsers = React.useCallback(async () => {
    try {
      const res = await apiClient.post<{ users: OrgUser[] }>(
        "/user/org-users",
        {}
      );
      setOrgUsers(res.users);
    } catch (error) {
      console.error("Error fetching org users:", error);
    }
  }, []);

  // Fetch reminder types and org users on mount
  useEffect(() => {
    fetchReminderTypes();
    fetchOrgUsers();
  }, [fetchOrgUsers]);

  const loadReminders = useCallback(
    async (isLoadMore: boolean) => {
      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setError(null);
        }

        const requestBody: any = {
          limit: ITEMS_PER_PAGE,
          offset: isLoadMore ? reminders.length : 0,
          status: "pending", // Only fetch pending reminders
        };

        // Add tag filter if present
        if (tag) {
          requestBody.tag = tag;
        }

        if (filterUserIds.length > 0) {
          requestBody.userIds = filterUserIds;
        }

        // If filterTypes is empty, don't fetch any reminders
        // If filterTypes has some (but not all) types, filter by those types
        // If filterTypes has all types, don't add filter (show all)
        if (filterTypes.length === 0) {
          // User deselected all - show no results
          if (!isLoadMore) {
            setReminders([]);
            setTotal(0);
            setHasMore(false);
          }
          setLoading(false);
          setLoadingMore(false);
          return;
        } else if (filterTypes.length < reminderTypes.length) {
          requestBody.types = filterTypes;
        }

        if (startDate) {
          requestBody.fromDate = startDate.toISOString();
        }

        if (endDate) {
          requestBody.toDate = endDate.toISOString();
        }

        const res = await apiClient.post<{
          reminders: ReminderWithUser[];
          total: number;
          offset: number;
          limit: number;
        }>("/shipments/reminders/list-reminders", requestBody);

        if (isLoadMore) {
          // Append new reminders
          setReminders((prev) => [...prev, ...res.reminders]);
        } else {
          // Replace with new reminders
          setReminders(res.reminders);
        }

        setTotal(res.total);

        // Check if there are more items to load
        const newTotal = isLoadMore
          ? reminders.length + res.reminders.length
          : res.reminders.length;
        setHasMore(newTotal < res.total);
      } catch (error) {
        console.error("Error fetching reminders:", error);
        setError("Failed to load exceptions");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      tag,
      filterUserIds,
      filterTypes,
      reminderTypes.length,
      startDate,
      endDate,
      reminders.length,
      ITEMS_PER_PAGE,
    ]
  );

  const handleSearch = () => {
    loadReminders(false);
  };

  const handleResetFilters = () => {
    setFilterUserIds([]);
    setFilterTypes(reminderTypes.map((t) => t.type));
    setStartDate(subDays(new Date(), 30));
    setEndDate(new Date());
  };

  const handleShipmentClick = (shipmentId: string) => {
    if (onShipmentClick) {
      onShipmentClick(shipmentId);
    }
  };

  const handleLoadMore = () => {
    loadReminders(true);
  };

  const handleReminderUpdated = (reminderId: string, resolved: boolean) => {
    // Update the reminder in the list
    setReminders((prev) =>
      prev.map((r) => {
        if (r.id === reminderId) {
          return {
            ...r,
            status: resolved ? "resolved" : "pending",
            resolvedTime: resolved ? new Date() : null,
          };
        }
        return r;
      })
    );
  };

  // Helper to get reminder category from reminderTypes
  const getReminderCategory = useCallback(
    (reminderType: string): "exception" | "milestone" | null => {
      const type = reminderTypes.find((t) => t.type === reminderType);
      return type?.category ?? null;
    },
    [reminderTypes]
  );

  // Organize reminders into sections with separators
  useEffect(() => {
    const now = new Date();
    const sections: ReminderSection[] = [];
    let currentSection: ReminderWithUser[] = [];
    let hasShownOneDaySeparator = false;
    let hasShownOneWeekSeparator = false;

    // Filter reminders based on category filters
    const filteredReminders = reminders.filter((reminder) => {
      const category = getReminderCategory(reminder.type);
      if (category === "exception" && !showExceptions) return false;
      if (category === "milestone" && !showMilestones) return false;
      return true;
    });

    filteredReminders.forEach((reminder) => {
      if (!reminder.triggerTime) {
        currentSection.push(reminder);
        return;
      }

      const hoursSince = differenceInHours(now, new Date(reminder.triggerTime));
      const daysSince = differenceInDays(now, new Date(reminder.triggerTime));

      // Check if we need to insert a separator
      if (!hasShownOneWeekSeparator && daysSince >= 7) {
        // Add current section before separator
        if (currentSection.length > 0) {
          sections.push({
            type: "reminders",
            reminders: currentSection,
          });
          currentSection = [];
        }
        sections.push({
          type: "separator",
          label: "More than a week ago",
        });
        hasShownOneWeekSeparator = true;
        hasShownOneDaySeparator = true; // Skip the one day separator
      } else if (!hasShownOneDaySeparator && hoursSince >= 24) {
        // Add current section before separator
        if (currentSection.length > 0) {
          sections.push({
            type: "reminders",
            reminders: currentSection,
          });
          currentSection = [];
        }
        sections.push({
          type: "separator",
          label: "More than one day ago",
        });
        hasShownOneDaySeparator = true;
      }

      currentSection.push(reminder);
    });

    // Add final section
    if (currentSection.length > 0) {
      sections.push({ type: "reminders", reminders: currentSection });
    }

    setReminderSections(sections);
  }, [reminders, showExceptions, showMilestones, getReminderCategory]);

  // Fetch on mount when reminderTypes are loaded
  useEffect(() => {
    if (reminderTypes.length > 0) {
      loadReminders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderTypes.length]); // Only run when reminderTypes are loaded

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
          <NotificationsIcon sx={{ fontSize: 20 }} />
          <Typography variant="h4">Reminders Dashboard</Typography>
        </Stack> */}

        {/* Filters Section */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            sx={{ mb: 2, rowGap: 2 }}
            flexWrap="wrap"
          >
            {/* Operator Filter */}
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Assigned To</InputLabel>
              <Select
                multiple
                value={filterUserIds}
                label="Assigned To"
                onChange={(e) => setFilterUserIds(e.target.value as string[])}
                input={<OutlinedInput label="Assigned To" />}
                renderValue={(selected) => {
                  if (selected.length === 0) return "All Users";
                  if (selected.length === orgUsers.length) return "All Users";
                  return `${selected.length} selected`;
                }}
              >
                {orgUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Checkbox checked={filterUserIds.includes(user.id)} />
                    <ListItemText primary={user.email} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Type Filter */}
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Type</InputLabel>
              <Select
                multiple
                value={filterTypes}
                label="Exception Type"
                onChange={(e) => {
                  const value = e.target.value as string[];

                  // Handle special values
                  if (value.includes("__SELECT_ALL__")) {
                    setFilterTypes(reminderTypes.map((t) => t.type));
                  } else if (value.includes("__DESELECT_ALL__")) {
                    setFilterTypes([]);
                  } else {
                    // Normal selection
                    setFilterTypes(value);
                  }
                }}
                input={<OutlinedInput label="Exception Type" />}
                renderValue={(selected) => {
                  if (selected.length === 0) return "All Types";
                  if (selected.length === reminderTypes.length)
                    return "All Types";
                  return `${selected.length} selected`;
                }}
              >
                <MenuItem value="__SELECT_ALL__">
                  <ListItemText primary="Select All" />
                </MenuItem>
                <MenuItem value="__DESELECT_ALL__">
                  <ListItemText primary="Deselect All" />
                </MenuItem>
                <Divider />
                {reminderTypes.map((type) => (
                  <MenuItem key={type.type} value={type.type}>
                    <Checkbox checked={filterTypes.includes(type.type)} />
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range Filters */}
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
            />

            {/* Action Buttons */}
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleResetFilters}>
              Reset
            </Button>
          </Stack>
        </Box>

        {/* Category Filter Chips */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                mr: 1,
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "text.secondary",
              }}
            >
              Show:
            </Typography>

            {/* Exceptions Chip */}
            <Chip
              icon={
                <Checkbox
                  checked={showExceptions}
                  size="small"
                  sx={{
                    color: showExceptions ? "#f57c00" : "rgba(0, 0, 0, 0.6)",
                    "&.Mui-checked": {
                      color: "#f57c00",
                    },
                    p: 0,
                    mr: -0.5,
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BellIcon sx={{ fontSize: 16 }} />
                  <span>Reminders</span>
                </Box>
              }
              onClick={() => setShowExceptions(!showExceptions)}
              variant="outlined"
              sx={{
                borderColor: "#ffa726",
                backgroundColor: showExceptions ? "#fff3e0" : "transparent",
                color: showExceptions ? "#f57c00" : "rgba(0, 0, 0, 0.87)",
                "&:hover": {
                  backgroundColor: showExceptions
                    ? "#ffe0b2"
                    : "rgba(255, 167, 38, 0.08)",
                  borderColor: "#f57c00",
                },
                "& .MuiChip-icon": {
                  ml: 1,
                },
              }}
            />

            {/* Milestones Chip */}
            <Chip
              icon={
                <Checkbox
                  checked={showMilestones}
                  size="small"
                  sx={{
                    color: showMilestones ? "#4caf50" : "rgba(0, 0, 0, 0.6)",
                    "&.Mui-checked": {
                      color: "#4caf50",
                    },
                    p: 0,
                    mr: -0.5,
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <FlagIcon sx={{ fontSize: 16 }} />
                  <span>Milestones</span>
                </Box>
              }
              onClick={() => setShowMilestones(!showMilestones)}
              variant="outlined"
              sx={{
                borderColor: "#4caf50",
                backgroundColor: showMilestones
                  ? "rgba(76, 175, 80, 0.08)"
                  : "transparent",
                color: showMilestones ? "#4caf50" : "rgba(0, 0, 0, 0.87)",
                "&:hover": {
                  backgroundColor: showMilestones
                    ? "rgba(76, 175, 80, 0.15)"
                    : "rgba(76, 175, 80, 0.04)",
                  borderColor: "#388e3c",
                },
                "& .MuiChip-icon": {
                  ml: 1,
                },
              }}
            />
          </Stack>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading or Reminders */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Recent Milestones and Exceptions */}
            {reminders.length > 0 && (
              <Box sx={{ mb: 4 }}>
                {reminderSections.map((section, idx) => {
                  if (section.type === "separator") {
                    return (
                      <Box
                        key={`separator-${idx}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          my: 3,
                        }}
                      >
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            mx: 2,
                            color: "text.secondary",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                          }}
                        >
                          {section.label}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={`reminders-${idx}`}
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {section.reminders?.map((reminder) => (
                        <ShipmentReminderComponent
                          key={reminder.id}
                          reminder={reminder}
                          showShipmentOverviewInfo={true}
                          onEmailOpen={(id) => setOpenEmailId(id)}
                          onShipmentClick={
                            onShipmentClick
                              ? (shipmentId: string) =>
                                  handleShipmentClick(shipmentId)
                              : undefined
                          }
                          onReminderUpdated={handleReminderUpdated}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* No reminders message */}
            {reminders.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 4,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No pending exceptions found
                </Typography>
              </Box>
            )}

            {/* Load More Button */}
            {reminders.length > 0 && hasMore && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                {loadingMore ? (
                  <CircularProgress size={32} />
                ) : (
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            )}

            {/* End of list message */}
            {reminders.length > 0 && !hasMore && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No more reminders to load
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Email Dialog */}
        {openEmailId && (
          <Dialog
            open={Boolean(openEmailId)}
            onClose={handleCloseEmail}
            fullWidth
            maxWidth="lg"
            sx={{
              "& .MuiDialog-paper": {
                height: "90vh",
              },
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleCloseEmail}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: 0 }}>
              <EmailThreadComponent
                emailId={openEmailId}
                sx={{ height: "100%" }}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export const ExceptionsDashboard: React.FC = () => {
  return (
    <DobbyShipmentPage title="Reminders Dashboard">
      <ExceptionsDashboardContent />
    </DobbyShipmentPage>
  );
};
